import { useEffect } from "react";
import axios from "axios";
import { API } from "@/lib/api";

/**
 * Fetches the admin-configured head injection code for the homepage and
 * appends it to <head> when the homepage mounts.
 *
 * SECURITY MODEL — intentional by design:
 *   The injected HTML comes from an admin-password-protected endpoint
 *   (PUT /api/admin/site-settings). The whole purpose of this feature is to
 *   allow the site owner to paste arbitrary <script>/<meta>/<link> tags
 *   (Google Analytics, Search Console verification, Meta Pixel, etc).
 *   Sanitizing with DOMPurify would strip exactly the tags this feature is
 *   meant to inject and break the use case. Trust boundary = admin password.
 *
 * Safely handles:
 *  - <script> tags (cloned so the browser executes them)
 *  - <meta>, <link>, <style>, etc. (moved as-is)
 *  - Cleanup on unmount (removes only the nodes we added)
 */
const useHomepageHeadInjection = () => {
  useEffect(() => {
    let mounted = true;
    const addedNodes = [];

    const inject = async () => {
      try {
        const { data } = await axios.get(`${API}/site-settings`);
        const html = data?.head_inject_homepage?.trim();
        if (!mounted || !html) return;

        const template = document.createElement("template");
        template.innerHTML = html;
        Array.from(template.content.childNodes).forEach((node) => {
          if (node.nodeType !== Node.ELEMENT_NODE) return;
          let target = node;
          // <script> nodes inserted via innerHTML do not execute. Recreate them.
          if (node.tagName === "SCRIPT") {
            target = document.createElement("script");
            Array.from(node.attributes).forEach((attr) =>
              target.setAttribute(attr.name, attr.value),
            );
            target.text = node.text || node.textContent || "";
          }
          document.head.appendChild(target);
          addedNodes.push(target);
        });
      } catch (err) {
        console.warn("[useHomepageHeadInjection] Head injection failed:", err);
      }
    };

    inject();

    return () => {
      mounted = false;
      addedNodes.forEach((n) => {
        if (n.parentNode) n.parentNode.removeChild(n);
      });
    };
  }, []);
};

export default useHomepageHeadInjection;
